import { fabric } from 'fabric';

// The editor's Fabric extensions (padded text plates, serialised layer roles)
// must be installed here too, or a scene the editor saved would draw
// differently when flattened without it.
import i18n from 'i18next';

import '../components/design-editor/fabric-setup';
import { loadFontsFor } from '../components/design-editor/fonts';
import { buildTemplateScene, isTemplateId } from '../components/design-editor/template-scene';

/**
 * Flatten a design scene into a PNG without opening the editor.
 *
 * The Design tool composes text over a picture in a Fabric canvas, and the
 * finished node needs `renderedUrl` — the flattened image — before it can run.
 * That URL only ever came from a vendor opening the editor and pressing Save,
 * which is fine when a person is doing the designing and useless when the agent
 * is: a plan could lay the words out perfectly and the node would sit there
 * doing nothing, because nothing had rasterised it.
 *
 * Same output as the editor's Save, minus the editor.
 */
export async function renderDesignScene(
    sourceImageUrl: string,
    fabricJson: any,
    width: number,
    height: number,
): Promise<{ blob: Blob; scene: any }> {
    if (!width || !height) throw new Error('Design render needs the source image size');

    // A plan that asked for one of the editor's templates: lay the template out
    // on this picture first, then draw it exactly as the editor would.
    if (fabricJson?.csTemplate && isTemplateId(fabricJson.csTemplate.id)) {
        const scene = await buildTemplateScene({
            request: fabricJson.csTemplate,
            imageUrl: sourceImageUrl,
            imageWidth: width,
            imageHeight: height,
            t: (key, options) => String(i18n.t(key, options as any)),
        });
        try { await loadFontsFor(scene.objects || []); } catch { /* fall back to the stack */ }
        return renderSelfContained(scene);
    }

    // Measure text against the real webfont. Rendering before the fonts have
    // arrived lays the type out in the fallback and bakes that into the picture.
    // Loading them explicitly matters: a canvas never triggers a webfont
    // download on its own, so `fonts.ready` alone resolved with the brand face
    // still missing.
    const sceneObjects = Array.isArray(fabricJson?.objects) ? fabricJson.objects : [];
    try { await loadFontsFor(sceneObjects); } catch { /* fall back to the stack */ }
    try { await (document as any).fonts?.ready; } catch { /* not supported; carry on */ }

    // A scene saved by the editor is self-contained: it carries its own
    // artboard size and the picture as its bottom layer.
    if (fabricJson?.csScene === 2 && fabricJson.artboardWidth && fabricJson.artboardHeight) {
        return renderSelfContained(fabricJson);
    }

    const canvas = new fabric.StaticCanvas(null as any, {
        width,
        height,
        enableRetinaScaling: false,
    });

    try {
        // The picture being designed on. crossOrigin matters: without it the
        // canvas is tainted and toDataURL throws rather than returning a
        // half-right image, which at least fails honestly.
        await new Promise<void>((resolve, reject) => {
            fabric.Image.fromURL(
                sourceImageUrl,
                (img) => {
                    if (!img) { reject(new Error('Could not load the image to design on')); return; }
                    img.set({ left: 0, top: 0, originX: 'left', originY: 'top' });
                    if (img.width) img.scaleToWidth(width);
                    canvas.setBackgroundImage(img, () => resolve());
                },
                { crossOrigin: 'anonymous' },
            );
        });

        const objects = Array.isArray(fabricJson?.objects) ? fabricJson.objects : [];
        if (objects.length > 0) {
            await new Promise<void>((resolve) => {
                fabric.util.enlivenObjects(objects, (live: any[]) => {
                    live.forEach((o, i) => {
                        // A logo is placed once its real proportions are known —
                        // scaled to a width and pushed into its corner, so a wide
                        // wordmark stays wide instead of being squashed square.
                        const fit = (objects[i] as any)?.fitLogo;
                        if (fit && typeof (o as any).scaleToWidth === 'function') {
                            (o as any).scaleToWidth(fit.width);
                            const h = (o as any).getScaledHeight?.() ?? fit.width;
                            o.set({
                                left: fit.atLeft ? fit.margin : fit.frameW - fit.margin - fit.width,
                                top: fit.atTop ? fit.margin : fit.frameH - fit.margin - h,
                            });
                        }
                        canvas.add(o);
                    });
                    resolve();
                }, '');
            });
        }

        canvas.renderAll();

        // Read the canvas element directly rather than going out through a
        // data: URL. Fetching one of those is blocked by the app's CSP, so the
        // scene rendered perfectly and then died on the way to a Blob.
        const el: HTMLCanvasElement = (canvas as any).getElement();
        const blob = await new Promise<Blob | null>((resolve) => {
            el.toBlob((b) => resolve(b), 'image/png');
        });
        if (!blob || !blob.size) throw new Error('Design render produced an empty image');

        // Hand back the scene as it was actually drawn.
        //
        // A logo's scale and corner can only be worked out once its bitmap has
        // loaded, so the JSON that came in still had it at natural size in the
        // top-left. Storing that meant the flattened picture was right and the
        // editor opened on a different composition — a giant logo across the
        // headline — which is the same thing being described two ways. What was
        // rendered is what gets stored.
        // `authoredWidth` travels with the scene because the editor lays layers
        // out in display pixels, not source pixels, and this one was composed at
        // the picture's full size. Without it the editor opens on the right
        // composition at the wrong scale — a headline running off the frame.
        const scene = {
            version: '5.3.0',
            authoredWidth: width,
            objects: (canvas.toObject() as any).objects || [],
        };
        return { blob, scene };
    } finally {
        canvas.dispose();
    }
}

/** Flatten an editor-saved scene (csScene 2) exactly as the editor exported it. */
async function renderSelfContained(fabricJson: any): Promise<{ blob: Blob; scene: any }> {
    const width = fabricJson.artboardWidth;
    const height = fabricJson.artboardHeight;
    const canvas = new fabric.StaticCanvas(null as any, { width, height, enableRetinaScaling: false });
    try {
        await new Promise<void>((resolve) => {
            canvas.loadFromJSON({ ...fabricJson, backgroundImage: undefined, clipPath: undefined }, () => resolve());
        });
        canvas.renderAll();
        const el: HTMLCanvasElement = (canvas as any).getElement();
        const blob = await new Promise<Blob | null>((resolve) => { el.toBlob((b) => resolve(b), 'image/png'); });
        if (!blob || !blob.size) throw new Error('Design render produced an empty image');
        return { blob, scene: fabricJson };
    } finally {
        canvas.dispose();
    }
}

/**
 * Development hook: run a plan's design spec through the same path the
 * workspace uses for agent designs (designSpecToFabric → renderDesignScene)
 * and hand back the PNG, so template output can be checked without a plan.
 *   await window.__csRenderDesignSpec(spec, imageUrl, width, height, brand?, logoUrl?)
 */
if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
    (window as any).__csRenderDesignSpec = async (
        spec: any, imageUrl: string, width: number, height: number, brand?: any, logoUrl?: string,
    ) => {
        const { designSpecToFabric } = await import('../planner/plan-compiler');
        const fabricJson = designSpecToFabric(spec, width, height, logoUrl, brand);
        const { blob, scene } = await renderDesignScene(imageUrl, fabricJson, width, height);
        const dataUrl = await new Promise<string>((resolve) => {
            const r = new FileReader();
            r.onload = () => resolve(String(r.result));
            r.readAsDataURL(blob);
        });
        return { dataUrl, bytes: blob.size, width: scene.artboardWidth || width, height: scene.artboardHeight || height, layers: (scene.objects || []).length, csScene: scene.csScene };
    };
}
